import { MutableRefObject, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useMedia } from 'react-use';

import { useIsDarkmode } from '../../../hooks/useIsDarkmode';
import { useIsDocumentReady } from '../../../hooks/useIsDocumentReady';
import { fontStyles } from '../../../styles/typography';
import { breakpoints } from '../../../styles/util';
import { classNames } from '../../../utils/classnames';
import { SearchBar } from '../SearchBar';

// to identify if a user re-entered a tab after a long pause
const maxDeltaTime = 1000;

class Particle {
  // start offset from the left or right
  dx: number;
  dy: number;
  vx: number;
  size: number;
  direction: 'left' | 'right';
  life: number;

  constructor(dx: number, dy: number, direction: 'left' | 'right') {
    this.dx = dx;
    this.dy = dy;
    // between 20 - 60 pixels per second
    this.vx = Math.random() * 40 + 20;
    // radius between 1-2 pixels
    this.size = Math.random() * 1.5 + 0.5;
    this.direction = direction;

    // the particles live a maximum of 10 seconds
    this.life = 10000;
  }

  update(deltaTime: number): void {
    this.dx += this.vx * (deltaTime / 1000);

    this.life -= deltaTime;
  }

  draw(ctx: CanvasRenderingContext2D, canvasWidth: number): void {
    const x = this.direction === 'left' ? canvasWidth - this.dx : this.dx;
    const y = this.dy + Math.sin(x / 100) * 20;
    // the particle fades in over 0.5 seconds and starts to fade out gradually
    // as it reaches a 100 pixel distance from the center of the canvas
    const center = canvasWidth / 2;
    const distanceFromCenter = center - this.dx;

    let alpha = 1;
    if (this.life > 9000) {
      const progress = this.life - 9000;
      alpha = 1 - progress / 1000;
    } else if (this.life < 500) {
      alpha = this.life / 500;
    }

    if (distanceFromCenter < 0) {
      alpha = 0;
    } else if (distanceFromCenter < 100) {
      alpha = distanceFromCenter / 100;
    }

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = `rgb(108,190,137)`;
    ctx.beginPath();
    ctx.arc(x, y, this.size, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  isDead(): boolean {
    return this.life <= 0;
  }
}

class ParticleEmitter {
  particles: Set<Particle>;
  featherSize: number;
  emissionRate: number;
  lastEmissionTime: number = 0;
  configurationRef: RefObject<{
    height: number;
    width: number;
    offsetTop: number;
    offsetBottom: number;
    offsetSide: number;
  }>;
  direction: 'left' | 'right';

  constructor(
    configurationRef: RefObject<{
      height: number;
      width: number;
      offsetTop: number;
      offsetBottom: number;
      offsetSide: number;
    }>,
    direction: 'left' | 'right',
  ) {
    this.particles = new Set();
    this.featherSize = 50;
    this.direction = direction;
    this.emissionRate = 2;
    this.configurationRef = configurationRef;
  }

  emit(): void {
    const configRef = this.configurationRef.current;
    if (!configRef) return;
    // randomly choose a point within the emission area (absolute offset from the left or right)
    const dx = Math.random() * (configRef.offsetSide - this.featherSize) + this.featherSize;
    const dy =
      Math.random() * (configRef.height - configRef.offsetBottom - configRef.offsetTop) +
      configRef.offsetTop;

    // create a new particle
    const particle = new Particle(dx, dy, this.direction);
    this.particles.add(particle);
  }

  update(deltaTime: number): void {
    // Emit new particles based on emission rate
    this.lastEmissionTime += deltaTime;
    const emissionInterval = 1000 / this.emissionRate;

    while (this.lastEmissionTime >= emissionInterval) {
      this.emit();
      this.lastEmissionTime -= emissionInterval;
    }

    // update all particles
    for (const particle of this.particles) {
      particle.update(deltaTime);

      // remove dead particles
      if (particle.isDead()) {
        this.particles.delete(particle);
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const configRef = this.configurationRef.current;
    if (!configRef) return;
    this.particles.forEach((particle) => particle.draw(ctx, configRef.width));
  }
}

const colors = {
  light: {
    gradientBrightFull: 'rgba(196,242,157,1)',
    gradientBrightTransparent: 'rgba(196,242,157,0)',
    gradientBgFull: 'rgba(108,190,137,1)',
    gradientBgTransparent: 'rgba(108,190,137,0)',
  },
  dark: {
    gradientBrightFull: 'rgba(129, 221, 113, 1)',
    gradientBrightTransparent: 'rgba(129, 221, 113, 0)',
    gradientBgFull: 'rgba(64, 133, 88, 1)',
    gradientBgTransparent: 'rgba(64, 133, 88, 0)',
  },
};

const globalAnimation = `
 div#app {
  background: none;
  position: relative;
  background-color: var(--color-special-background-app);
 }

div#app::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: var(--gradient-token-homepage);
  pointer-events: none;
}
`;

const blurBase = classNames(
  'absolute',
  'h-full',
  'w-[102px]',
  'backdrop-blur-[2px]',
  'even:backdrop-blur-none',
  'tablet:even:backdrop-blur-[7px]',
);

export function Hero() {
  // Calculate initial search bar width based on CSS formula: min(600px, 80vw)
  const getSearchBarWidth = () => {
    if (typeof window !== 'undefined') {
      return Math.min(600, window.innerWidth * 0.8);
    }
    return 300; // fallback
  };

  const [containerWidth, setContainerWidth] = useState(0);
  const configRef = useRef<{
    height: number;
    width: number;
    offsetTop: number;
    offsetSide: number;
    offsetBottom: number;
  }>({ height: 0, width: 0, offsetTop: 0, offsetSide: 0, offsetBottom: 0 });
  const animationWrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const emitterRightRef = useRef<ParticleEmitter>(null);
  const emitterLeftRef = useRef<ParticleEmitter>(null);
  const [showParticles, setShowParticles] = useState(false);
  const isDocumentReady = useIsDocumentReady();
  const isDarkmode = useIsDarkmode();
  const isMobile = useMedia(breakpoints.mobile, false);
  const [searchBarWidth, setSearchBarWidth] = useState(getSearchBarWidth());

  const [circleAnimationState, setCircleAnimationState] = useState<{
    animating: boolean;
    done: boolean;
  }>({ animating: false, done: false });

  // Update searchBarWidth when ref becomes available or window resizes
  useEffect(() => {
    const updateSearchBarWidth = () => {
      setSearchBarWidth(getSearchBarWidth());
    };

    // Initial measurement
    updateSearchBarWidth();

    // Update on resize
    window.addEventListener('resize', updateSearchBarWidth);
    return () => window.removeEventListener('resize', updateSearchBarWidth);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setContainerWidth(window.innerWidth);
      configRef.current.width = window.innerWidth;
      configRef.current.height = window.innerWidth < 767 ? 150 : 300;
      configRef.current.offsetTop = window.innerWidth < 767 ? 5 : 80;
      configRef.current.offsetBottom = window.innerWidth < 767 ? 80 : 100;
      configRef.current.offsetSide = window.innerWidth < 767 ? 50 : 150;
    };
    window.addEventListener('resize', handleResize);
    // run once
    handleResize();

    if (isDocumentReady) {
      setTimeout(() => {
        setShowParticles(true);
      }, 300);
    }

    return () => window.removeEventListener('resize', handleResize);
  }, [isDocumentReady]);

  // Start circle animation when ready signal is received
  useEffect(() => {
    if (!isDocumentReady) return;

    setTimeout(() => {
      setCircleAnimationState({ animating: true, done: false });
    }, 300);

    setTimeout(() => {
      setCircleAnimationState({ animating: false, done: true });
    }, 2000);
  }, [isDocumentReady]);

  useEffect(() => {
    if (!showParticles) return;

    const section = animationWrapperRef.current;
    if (!section) return;

    let canvas = canvasRef.current;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.filter = 'blur(2px)';
      (canvasRef as MutableRefObject<HTMLCanvasElement>).current = canvas;
      section.appendChild(canvas);
    }
    canvas.width = configRef.current.width;
    canvas.height = configRef.current.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let emitterRight = emitterRightRef.current;
    let emitterLeft = emitterLeftRef.current;

    if (!emitterRight) {
      emitterRight = new ParticleEmitter(configRef, 'right');
      (emitterRightRef as MutableRefObject<ParticleEmitter>).current = emitterRight;
    }
    if (!emitterLeft) {
      emitterLeft = new ParticleEmitter(configRef, 'left');
      (emitterLeftRef as MutableRefObject<ParticleEmitter>).current = emitterLeft;
    }

    let lastTime = -1;

    let animationFrameId: number;

    function animate(currentTime: number) {
      if (ctx && emitterRight && emitterLeft) {
        if (lastTime === -1 || currentTime - lastTime > maxDeltaTime) {
          lastTime = currentTime;
        }
        const deltaTime = currentTime - lastTime;
        lastTime = currentTime;

        ctx.clearRect(0, 0, configRef.current.width, configRef.current.height);
        emitterRight.update(deltaTime);
        emitterLeft.update(deltaTime);
        emitterRight.draw(ctx);
        emitterLeft.draw(ctx);
      }

      animationFrameId = requestAnimationFrame(animate);
    }

    animationFrameId = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrameId);
  }, [containerWidth, showParticles, isMobile]);

  const offsetTop = 10; // blur radius

  const paths = useMemo(() => {
    const horizontalLength = containerWidth - (isMobile ? 80 : 228);
    if (isMobile) {
      return {
        top: `M -3.5 ${offsetTop - 10} l 39 38.85 c 1.4 1.4 2.8 1.4 4.2 1.4 h ${horizontalLength} c 1.4 0 2.8 0 4.2 -1.4 l 39 -38.85 v 23.8 l -39 18.9 c -1.75 0.7 -2.45 0.7 -4.2 0.7 h -${horizontalLength} c -1.75 0 -2.45 0 -4.2 -0.7 l -39 -18.9 z`,
        middle: `M -3.5 ${offsetTop + 32 - 10} l 39 14 c 0.7 0.175 2.1 0.35 4.2 0.35 h ${horizontalLength} c 1.05 0 2.45 -0.175 3.15 -0.35 l 39 -14 v 31.5 l -39 -14 c -0.7 -0.175 -2.1 -0.35 -3.15 -0.35 h -${horizontalLength} c -1.05 0 -2.45 0.175 -3.15 0.35 l -39 14 z`,
        bottom: `M -3.5 ${offsetTop + 75 + 10} l 39 -38.85 c 1.4 -1.4 2.8 -1.4 4.2 -1.4 h ${horizontalLength} c 1.4 0 2.8 0 4.2 1.4 l 39 38.85 v -23.8 l -39 -18.9 c -1.75 -0.7 -2.45 -0.7 -4.2 -0.7 h -${horizontalLength} c -1.75 0 -2.45 0 -4.2 0.7 l -39 18.9 z`,
      };
    }
    return {
      top: `M -10 ${offsetTop - 10} l 112 111 c 4 4 8 4 12 4 h ${horizontalLength} c 4 0 8 0 12 -4 l 112 -111 v 68 l -112 54 c -5 2 -7 2 -12 2 h -${horizontalLength} c -5 0 -7 0 -12 -2 l -112 -54 z`,
      middle: `M -10 ${offsetTop + 86.5 - 10} l 112 40 c 2 0.5 6 1 12 1 h ${horizontalLength} c 3 0 7 -0.5 9 -1 l 112 -40 v 90 l -112 -40 c -2 -0.5 -6 -1 -9 -1 h -${horizontalLength} c -3 0 -7 0.5 -9 1 l -112 40 z`,
      bottom: `M -10 ${offsetTop + 244 + 10} l 112 -111 c 4 -4 8 -4 12 -4 h ${horizontalLength} c 4 0 8 0 12 4 l 112 111 v -68 l -112 -54 c -5 -2 -7 -2 -12 -2 h -${horizontalLength} c -5 0 -7 0 -12 2 l -112 54 z`,
    };
  }, [containerWidth, isMobile]);

  const themeColors = useMemo(() => {
    return isDarkmode ? colors.dark : colors.light;
  }, [isDarkmode]);

  return (
    <section
      className={classNames(
        'mt-[100px]',
        '[body[data-show-banner="true"]_&]:mt-[125px]',
        'relative',
        'tablet:[body[data-show-banner="true"]_&]:mt-[150px]',
        'tablet:mt-[125px]',
      )}
    >
      <style>{globalAnimation}</style>
      <h2
        className={fontStyles({
          variant: 'H2',
          class: [
            'text-[30px]',
            'h-20',
            'tablet:h-10',
            'font-semibold',
            'mx-auto',
            'text-center',
            'mb-0',
            'tablet:-mb-[70px]',
            'tablet:text-[40px]',
          ],
        })}
      >
        Swap any token <span className="text-token-gray-700 block tablet:inline">in seconds</span>
      </h2>
      <SearchBar position="hero" />
      <div className="relative pointer-events-none tablet:h-75 h-37.5" ref={animationWrapperRef}>
        {containerWidth > 0 && (
          <>
            <svg width={containerWidth} viewBox={`0 0 ${containerWidth} ${isMobile ? 150 : 300}`}>
              <defs>
                <clipPath id="hero-clip-path">
                  <path d={paths.top} />
                  <path d={paths.middle} />
                  <path d={paths.bottom} />
                </clipPath>
                <mask id="searchbar-mask" maskUnits="userSpaceOnUse">
                  <rect width="100%" height="100%" fill="white" />
                  {!isMobile && (
                    <circle
                      cx={containerWidth / 2}
                      cy={100}
                      r={searchBarWidth / 2 + 60}
                      fill="url(#mask-gradient)"
                    />
                  )}
                </mask>
                <radialGradient id="mask-gradient">
                  <stop offset="0.8" stopColor="black" />
                  <stop offset="1" stopColor="white" />
                </radialGradient>
                <radialGradient id="light-gradient">
                  <stop offset="0.4" stopColor={themeColors.gradientBrightFull} />
                  <stop offset="1" stopColor={themeColors.gradientBrightTransparent} />
                </radialGradient>
                <radialGradient id="light-gradient-sharp">
                  <stop offset="0.8" stopColor={themeColors.gradientBrightFull} />
                  <stop offset="1" stopColor={themeColors.gradientBrightTransparent} />
                </radialGradient>
                <linearGradient id="animatedGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0.35" stopColor={themeColors.gradientBgFull} />
                  <stop offset="0.4" stopColor={themeColors.gradientBrightTransparent} />
                  <stop offset="0.58" stopColor={themeColors.gradientBrightTransparent} />
                  <stop offset="0.6" stopColor={themeColors.gradientBgFull} />
                </linearGradient>
              </defs>
              <g mask={`url(#searchbar-mask)`}>
                <path fill="url(#animatedGradient)" d={paths.top} />
                <path fill="url(#animatedGradient)" d={paths.middle} />
                <path fill="url(#animatedGradient)" d={paths.bottom} />{' '}
                <circle
                  cx={containerWidth / 2}
                  cy={100}
                  r={searchBarWidth / 2 + (isMobile ? 50 : 100)}
                  fill="url(#light-gradient-sharp)"
                  clipPath="url(#hero-clip-path)"
                  opacity={0}
                  mask={`url(#searchbar-mask)`}
                >
                  <animate attributeName="opacity" to="1" dur="0.5s" begin="0.35s" fill="freeze" />
                </circle>
                <circle
                  cx={
                    !circleAnimationState.animating && !circleAnimationState.done
                      ? -150
                      : containerWidth / 2 - searchBarWidth / 2 - 50
                  }
                  cy={100}
                  r={isMobile ? 50 : 250}
                  fill="url(#light-gradient)"
                  clipPath="url(#hero-clip-path)"
                  className={classNames(
                    circleAnimationState.animating
                      ? '[transition:cx_2s_cubic-bezier(0.43,1,0.35,1)]'
                      : '',
                  )}
                />
                <circle
                  cx={
                    !circleAnimationState.animating && !circleAnimationState.done
                      ? containerWidth + 150
                      : containerWidth / 2 + searchBarWidth / 2 + 50
                  }
                  cy={100}
                  r={isMobile ? 50 : 250}
                  fill="url(#light-gradient)"
                  clipPath="url(#hero-clip-path)"
                  className={classNames(
                    circleAnimationState.animating
                      ? '[transition:cx_2s_cubic-bezier(0.43,1,0.35,1)]'
                      : '',
                  )}
                />
              </g>
            </svg>
            {/* Blur stack */}
            <div
              className={classNames(
                blurBase,
                'left-0',
                'top-0',
                'mask-[linear-gradient(to_right,black_0%,rgba(0,0,0,0.3)_85%,transparent_100%)]',
              )}
            />
            <div
              className={classNames(
                blurBase,
                'left-0',
                'top-0',
                'mask-[linear-gradient(to_right,black_0%,rgba(0,0,0,0.3)_85%,transparent_100%)]',
              )}
            />
            <div
              className={classNames(
                blurBase,
                'right-0',
                'top-0',
                'mask-[linear-gradient(to_left,black_0%,rgba(0,0,0,0.3)_85%,transparent_100%)]',
              )}
            />
            <div
              className={classNames(
                blurBase,
                'right-0',
                'top-0',
                'mask-[linear-gradient(to_left,black_0%,rgba(0,0,0,0.3)_85%,transparent_100%)]',
              )}
            />
          </>
        )}
      </div>
    </section>
  );
}
